import React, { useState, useEffect } from "react";
import "./MyAccountModal.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faUser } from "@fortawesome/free-solid-svg-icons";
import { useAuthContext } from "../Auth/AuthContext";
import {
  Mail,
  Phone,
  Camera,
  Trash2,
  Edit3,
  LogOut,
  X,
  Check,
  GraduationCap,
  Building2,
  Award,
  FolderGit2,
  BookOpen,
  Target,
  Code,
  Briefcase,
} from "lucide-react";
import config from "../../config.json";
import { CustomSelect } from "../CustomStyling";

function formatPhoneNumber(phoneNumber) {
  const cleaned = ("" + phoneNumber).replace(/\D/g, "");
  const match = cleaned.match(/^(\d{1,3})?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const countryCode = match[1] ? `+${match[1]} ` : "";
    return `${countryCode}(${match[2]}) ${match[3]}-${match[4]}`;
  }
  return phoneNumber;
}

function capitalizeFirstLetter(string) {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

const MyAccountModal = ({ handleSignOut }) => {
  const {userObjId, profileData, setProfileData } =
    useAuthContext();

  // Profile States
  const [isEditing, setIsEditing] = useState(false);
  const [editedFirstName, setEditedFirstName] = useState(
    profileData?.first_name || ""
  );
  const [editedLastName, setEditedLastName] = useState(
    profileData?.last_name || ""
  );
  const [editedPhone, setEditedPhone] = useState(
    profileData?.phone_number || ""
  );

  // Image States
  const [profileImage, setProfileImage] = useState(profileData?.profile_image);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  // Skills & Roles States (for working professionals)
  const [skills, setSkills] = useState(profileData?.skills || []);
  const [targetRoles, setTargetRoles] = useState(
    profileData?.target_roles || []
  );
  const [tempEducationLevel, setTempEducationLevel] = useState(profileData?.education_level || "");
  const [tempMajor, setTempMajor] = useState(profileData?.major || "");
  const [tempWorkingDetails, setTempWorkingDetails] = useState({
    currentJobTitle: profileData?.working_professional_details?.currentJobTitle || "",
    currentCompany: profileData?.working_professional_details?.currentCompany || "",
    totalExperience: profileData?.working_professional_details?.totalExperience || "",
  });
  const [skillInput, setSkillInput] = useState("");
  const [roleInput, setRoleInput] = useState("");
  const [tempSkills, setTempSkills] = useState([...skills]);
  const [tempRoles, setTempRoles] = useState([...targetRoles]);
  const [skillError, setSkillError] = useState("");
  const [roleError, setRoleError] = useState("");
  // Add after the existing state declarations

  const [studentSkillInput, setStudentSkillInput] = useState("");
  const [studentSkillError, setStudentSkillError] = useState("");
  const [nameError, setNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // Determine if student or working professional
  const isStudent = profileData?.professional_status === "student";
  const studentDetails = profileData?.student_details || {};
  const universityDetails =profileData?.university_details||{}

  const [tempStudentDetails, setTempStudentDetails] = useState({
    educationLevel: studentDetails?.educationLevel || "",
    department: studentDetails?.department || "",
    cgpa: studentDetails?.cgpa || "",
    yearOfGraduation: studentDetails?.yearOfGraduation || "",
    currentSemester: studentDetails?.currentSemester || "",
    skills: studentDetails?.skills || [],
    certificates: studentDetails?.certificates || [""],
    projects: studentDetails?.projects || [""],
  });

  useEffect(() => {
    setEditedFirstName(profileData?.first_name || "");
    setEditedLastName(profileData?.last_name || "");
    setEditedPhone(profileData?.phone_number || "");
    setSkills(profileData?.skills || []);
    setTempSkills(profileData?.skills || []);
    setTargetRoles(profileData?.target_roles || []);
    setTempRoles(profileData?.target_roles || []);
    setTempStudentDetails({
      educationLevel: profileData?.student_details?.educationLevel || "",
      department: profileData?.student_details?.department || "",
      cgpa: profileData?.student_details?.cgpa || "",
      yearOfGraduation: profileData?.student_details?.yearOfGraduation || "",
      currentSemester: profileData?.student_details?.currentSemester || "",
      skills: profileData?.student_details?.skills || [],
      certificates: profileData?.student_details?.certificates || [""],
      projects: profileData?.student_details?.projects || [""],
    });
    setTempEducationLevel(profileData?.education_level || "");
    setTempMajor(profileData?.major || "");
    setTempWorkingDetails({
      currentJobTitle: profileData?.working_professional_details?.currentJobTitle || "",
      currentCompany: profileData?.working_professional_details?.currentCompany || "",
      totalExperience: profileData?.working_professional_details?.totalExperience || "",
  });
  }, [profileData]);

  // Image preload
  useEffect(() => {
    if (profileData?.profile_image) {
      setImageLoading(true);
      const img = new Image();
      img.src = profileData.profile_image;
      img.onload = () => {
        setProfileImage(profileData.profile_image);
        setImageLoading(false);
      };
      img.onerror = () => {
        setProfileImage(null);
        setImageLoading(false);
      };
    }
  }, [profileData?.profile_image]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      setProfileImage(URL.createObjectURL(file));
    }
  };

  const handleImageDelete = () => {
    setProfileImage(null);
    setSelectedImage(null);
  };

  const triggerFileInput = () => {
    document.getElementById("profile-file-input").click();
  };

  const handleSkillAdd = (e) => {
    if (e.key === "Enter" && skillInput.trim()) {
      e.preventDefault();
      let newSkill = skillInput.trim();
      newSkill = newSkill.charAt(0).toUpperCase() + newSkill.slice(1);

      if (tempSkills.some((s) => s.toLowerCase() === newSkill.toLowerCase())) {
        setSkillError("Skill already added");
        return;
      }
      if (tempSkills.length >= 20) {
        setSkillError("Maximum 20 skills allowed");
        return;
      }
      if (newSkill.length > 50) {
        setSkillError("Skill must be under 50 characters");
        return;
      }

      setTempSkills([...tempSkills, newSkill]);
      setSkillInput("");
      setSkillError("");
    }
  };

  const handleRoleAdd = (e) => {
    if (e.key === "Enter" && roleInput.trim()) {
      e.preventDefault();
      let newRole = roleInput.trim();
      newRole = newRole.charAt(0).toUpperCase() + newRole.slice(1);

      if (tempRoles.some((r) => r.toLowerCase() === newRole.toLowerCase())) {
        setRoleError("Role already added");
        return;
      }
      if (tempRoles.length >= 20) {
        setRoleError("Maximum 20 roles allowed");
        return;
      }
      if (newRole.length > 50) {
        setRoleError("Role must be under 50 characters");
        return;
      }

      setTempRoles([...tempRoles, newRole]);
      setRoleInput("");
      setRoleError("");
    }
  };

  const handleStudentSkillAdd = (e) => {
    if (e.key === "Enter" && studentSkillInput.trim()) {
      e.preventDefault();
      let newSkill = studentSkillInput.trim();
      newSkill = newSkill.charAt(0).toUpperCase() + newSkill.slice(1);

      if (
        tempStudentDetails.skills.some(
          (s) => s.toLowerCase() === newSkill.toLowerCase()
        )
      ) {
        setStudentSkillError("Skill already added");
        return;
      }
      if (tempStudentDetails.skills.length >= 10) {
        setStudentSkillError("Maximum 10 skills allowed");
        return;
      }
      if (newSkill.length > 50) {
        setStudentSkillError("Skill must be under 50 characters");
        return;
      }

      setTempStudentDetails((prev) => ({
        ...prev,
        skills: [...prev.skills, newSkill],
      }));
      setStudentSkillInput("");
      setStudentSkillError("");
    }
  };

  const handleSave = async () => {
    setNameError("");
    setPhoneError("");
  
    // Validation
    let hasError = false;

    if (!editedFirstName.trim() || !editedLastName.trim()) {
      setNameError("Please enter your full name");
      hasError = true;
    }
  
    if (!editedPhone.trim()) {
      setPhoneError("Please enter your phone number");
      hasError = true;
    }
  
    if (hasError) return;

    const formData = new FormData();
    formData.append("user_obj_id", userObjId);
    formData.append("first_name", editedFirstName);
    formData.append("last_name", editedLastName);
    formData.append("phone_number", editedPhone);
    if (isStudent) {
      formData.append("student_details", JSON.stringify(tempStudentDetails));
    } else {
      formData.append("skills", tempSkills.join(","));
      formData.append("target_roles", tempRoles.join(","));
      formData.append("education_level", tempEducationLevel);
      formData.append("major", tempMajor);
      formData.append("working_professional_details", JSON.stringify(tempWorkingDetails));
    }

    if (selectedImage) {
      formData.append("image", selectedImage);
    } else if (!profileImage) {
      formData.append("image_flag", "N");
    }

    try {
      setIsUploading(true);
      const response = await fetch(
        `${config.restAPI}/accounts/updateUserProfile/`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      if (!response.ok) throw new Error("Profile update failed");
      const data = await response.json();

      const updatedProfileData = {
        ...profileData,
        first_name: editedFirstName,
        last_name: editedLastName,
        phone_number: editedPhone,
        // skills: tempSkills,
        // target_roles: tempRoles,
        skills: isStudent ? profileData.skills : tempSkills,
        target_roles: isStudent ? profileData.target_roles : tempRoles,
        student_details: isStudent
          ? tempStudentDetails
          : profileData.student_details,
        education_level: isStudent ? profileData.education_level : tempEducationLevel,
        major: isStudent ? profileData.major : tempMajor,
        working_professional_details: isStudent ? profileData.working_professional_details : tempWorkingDetails,
        profile_image: data.profile_image || profileData.profile_image,
        ...data,
      };

      setProfileData(updatedProfileData);
      setSkills(tempSkills);
      setTargetRoles(tempRoles);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setEditedFirstName(profileData?.first_name || "");
    setEditedLastName(profileData?.last_name || "");
    setEditedPhone(profileData?.phone_number || "");
    setTempSkills([...skills]);
    setTempRoles([...targetRoles]);
    setTempStudentDetails({
      educationLevel: profileData?.studentDetails?.educationLevel || "",
      department: profileData?.studentDetails?.department || "",
      cgpa: profileData?.studentDetails?.cgpa || "",
      skills: profileData?.studentDetails?.skills || [],
      certificates: profileData?.studentDetails?.certificates || [""],
      projects: profileData?.studentDetails?.projects || [""],
    });
    setTempEducationLevel(profileData?.education_level || "");
    setTempMajor(profileData?.major || "");
    setTempWorkingDetails({
      currentJobTitle: profileData?.working_professional_details?.currentJobTitle || "",
      currentCompany: profileData?.working_professional_details?.currentCompany || "",
      totalExperience: profileData?.working_professional_details?.totalExperience || "",
    });
    setProfileImage(profileData?.profile_image);
    setSelectedImage(null);
    setIsEditing(false);
  };

  const firstName = capitalizeFirstLetter(profileData?.first_name);
  const lastName = capitalizeFirstLetter(profileData?.last_name);

  const educationLevelMap = {
    high_school: "High School",
    bachelors: "Bachelor's Degree",
    masters: "Master's Degree",
    phd: "PhD",
  };

  const departmentMap = {
    computer_science: "Computer Science",
    electrical: "Electrical Engineering",
    mechanical: "Mechanical Engineering",
    civil: "Civil Engineering",
    business: "Business Administration",
    arts: "Arts & Humanities",
    science: "Natural Sciences",
    other: "Other",
  };

  // const getPlanIcon = (planType) => {
  //   switch (planType) {
  //     case "free-trial":
  //       return <Gift size={18} />;
  //     case "starter":
  //       return <Rocket size={18} />;
  //     default:
  //       return <Goal size={18} />;
  //   }
  // };

  // const getSessionTypeName = (type) => {
  //   const names = {
  //     prepbot: "PrepBot",
  //     mockbot: "MockBot",
  //     resume: "ResumeExpert",
  //     jd: "JDExpert",
  //   };
  //   return names[type] || type;
  // };

  return (
    <div className="modern-account-modal">
      {/* Header Section */}
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar-section">
            <div className="profile-avatar">
              {isUploading || imageLoading ? (
                <div className="avatar-loading">
                  <FontAwesomeIcon icon={faSpinner} spin />
                </div>
              ) : profileImage ? (
                <img src={profileImage} alt="Profile" />
              ) : (
                <FontAwesomeIcon icon={faUser} className="avatar-placeholder" />
              )}

              {isEditing && (
                <div className="avatar-overlay">
                  <button
                    className="avatar-btn camera"
                    onClick={triggerFileInput}
                  >
                    <Camera size={16} />
                  </button>
                  {profileImage && (
                    <button
                      className="avatar-btn delete"
                      onClick={handleImageDelete}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              )}
            </div>
            <input
              type="file"
              id="profile-file-input"
              hidden
              onChange={handleFileChange}
              accept="image/*"
            />

            <div className="user-profile-info">
              <h2 className="user-profile-name">
                {firstName} {lastName}
              </h2>
              {isStudent && universityDetails?.student_id && (
                <div className="user-profile-name">
                   ID: {universityDetails.student_id}
                </div>
              )}
              <div className="user-profile-badge">
                {isStudent ? (
                  <>
                    <GraduationCap size={14} /> Student
                  </>
                ) : (
                  <>
                    <Building2 size={14} /> Professional
                  </>
                )}
              </div>
            </div>
          </div>

          {/* <div className="profile-actions">
            {isEditing ? (
              <>
                <button
                  className="profile-action-btn save"
                  onClick={handleSave}
                  disabled={isUploading}
                >
                  <Check size={18} /> Save
                </button>
                <button
                  className="profile-action-btn cancel"
                  onClick={handleCancel}
                >
                  <X size={18} /> Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  className="profile-action-btn edit"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit3 size={18} /> Edit
                </button>
                <button
                  className="profile-action-btn logout"
                  onClick={handleSignOut}
                >
                  <LogOut size={18} /> Logout
                </button>
              </>
            )}
          </div> */}
            <>
              <button
              className="profile-action-btn logout"
              onClick={handleSignOut}
            >
              <LogOut size={18} /> Logout
            </button>
            </>
        </div>

        {/* Contact Info */}
        {!isEditing && (
          <div className="contact-info">
            <div className="contact-item">
              <Mail size={16} />
              <span>{profileData?.email||'N/A'}</span>
            </div>
            <div className="contact-item">
              <Phone size={16} />
              <span>{formatPhoneNumber(profileData?.phone_number)||'N/A'}</span>
            </div>
          </div>
        )}
      </div>

      {/* Content Grid */}
      <div className="profile-content-grid">
        {/* Edit Mode - Separate Fields */}
        {isEditing && (
          <div className="profile-card edit-fields-card">
            <div className="profile-card-header">
              {/* <Edit3 size={20} />
        <h3>Edit Profile</h3> */}
              <h3>Profile Details</h3>
            </div>
            <div className="edit-fields-grid">
              <div className="edit-field">
                <label>Full Name</label>
                <input
                  type="text"
                  value={`${editedFirstName} ${editedLastName}`}
                  onChange={(e) => {
                    const parts = e.target.value.split(" ");
                    setEditedFirstName(parts[0] || "");
                    setEditedLastName(parts.slice(1).join(" ") || "");
                    setNameError(""); // Clear error on change
                  }}
                  placeholder="Full Name"
                  className="edit-input"
                />
                  {nameError && <span className="tag-error">{nameError}</span>}
              </div>
              <div className="edit-field">
                <label>Email</label>
                <input
                  type="email"
                  value={profileData?.email || ""}
                  className="edit-input"
                  disabled
                />
              </div>
              <div className="edit-field">
                <label>Phone Number</label>
                <input
                  type="text"
                  value={editedPhone}
                  onChange={(e) => {
                    setEditedPhone(e.target.value);
                    setPhoneError("");
                  }}
                  placeholder="Phone Number"
                  className="edit-input"
                />
                {phoneError && <span className="tag-error">{phoneError}</span>}
              </div>
            </div>
          </div>
        )}

        {/* Student Details Section - View Mode Only */}
        {isStudent && (
          <div className="profile-card student-details-card">
            <div className="profile-card-header">
              <GraduationCap size={20} />
              <h3>Education Details</h3>
            </div>

            {isEditing ? (
              /* Edit Mode */
              <div className="student-edit-grid">
                <div className="edit-field-group">
                  <div className="edit-field">
                    {/* <label>Highest Degree</label> */}
                    <CustomSelect
                      label="Highest Degree"
                      value={tempStudentDetails.educationLevel}
                      placeholder="Select level"
                      options={[
                        { value: "high_school", label: "High School" },
                        { value: "bachelors", label: "Bachelor's" },
                        { value: "masters", label: "Master's" },
                        { value: "phd", label: "PhD" },
                      ]}
                      onChange={(val) =>
                        setTempStudentDetails((prev) => ({
                          ...prev,
                          educationLevel: val,
                        }))
                      }
                    />
                  </div>

                  <div className="edit-field">
                    <CustomSelect
                      label="Department"
                      value={tempStudentDetails.department}
                      placeholder="Select department"
                      options={[
                        {
                          value: "computer_science",
                          label: "Computer Science",
                        },
                        {
                          value: "electrical",
                          label: "Electrical Engineering",
                        },
                        {
                          value: "mechanical",
                          label: "Mechanical Engineering",
                        },
                        {
                          value: "business",
                          label: "Business Administration",
                        },
                        { value: "arts", label: "Arts & Humanities" },
                        { value: "science", label: "Natural Sciences" },
                        { value: "other", label: "Other" },
                      ]}
                      onChange={(val) =>
                        setTempStudentDetails((prev) => ({
                          ...prev,
                          department: val,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="edit-field-group">
                  <div className="edit-field">
                    <label>CGPA (0-10)</label>
                    <input
                      type="number"
                      className="edit-input"
                      placeholder="8.5"
                      min="0"
                      max="10"
                      step="0.1"
                      value={tempStudentDetails.cgpa}
                      onChange={(e) =>
                        setTempStudentDetails((prev) => ({
                          ...prev,
                          cgpa: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="edit-field">
                  <CustomSelect
                    label="Year of Graduation"
                    value={tempStudentDetails.yearOfGraduation}
                    placeholder="Select year"
                    scrollToValue={new Date().getFullYear().toString()}
                    options={Array.from({ length: 19 }, (_, i) => {
                      const currentYear = new Date().getFullYear();
                      const year = currentYear - 9 + i;
                      return { 
                        value: year.toString(), 
                        label: year === currentYear ? `${year} (Current)` : year.toString() 
                      };
                    })}
                    onChange={(val) =>
                      setTempStudentDetails((prev) => ({
                        ...prev,
                        yearOfGraduation: val,
                      }))
                    }
                  />
                  </div>

                  <div className="edit-field">
                    <label>Current Semester</label>
                    <input
                      type="number"
                      className="edit-input"
                      placeholder="1"
                      value={tempStudentDetails.currentSemester}
                      onChange={(e) =>
                        setTempStudentDetails((prev) => ({
                          ...prev,
                          currentSemester: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="edit-field full-width">
                  <label>Skills</label>
                  <input
                    type="text"
                    className="edit-input"
                    placeholder="Type skill and press Enter"
                    value={studentSkillInput}
                    onChange={(e) => {
                      setStudentSkillInput(e.target.value);
                      setStudentSkillError("");
                    }}
                    onKeyDown={handleStudentSkillAdd}
                  />
                  {studentSkillError && (
                    <span className="tag-error">{studentSkillError}</span>
                  )}
                  <div className="tags-container editable">
                    {tempStudentDetails.skills.map(
                      (skill, idx) =>
                        skill && (
                          <span key={idx} className="tag skill">
                            {skill}
                            <X
                              size={12}
                              onClick={() =>
                                setTempStudentDetails((prev) => ({
                                  ...prev,
                                  skills: prev.skills.filter(
                                    (_, i) => i !== idx
                                  ),
                                }))
                              }
                            />
                          </span>
                        )
                    )}
                  </div>
                </div>

                <div className="edit-field-group">
                  <div className="edit-field ">
                    <div className="education-field-header">
                      <label>Certificates</label>
                      <button
                        type="button"
                        className="add-item-btn"
                        onClick={() => {
                          if (tempStudentDetails.certificates.length < 5) {
                            setTempStudentDetails((prev) => ({
                              ...prev,
                              certificates: [...prev.certificates, ""],
                            }));
                          }
                        }}
                      >
                        + Add
                      </button>
                    </div>
                    {tempStudentDetails.certificates.map((cert, idx) => (
                      <div key={idx} className="input-with-remove">
                        <input
                          type="text"
                          className="edit-input"
                          placeholder="e.g., AWS Certified Developer"
                          value={cert}
                          onChange={(e) => {
                            const updated = [
                              ...tempStudentDetails.certificates,
                            ];
                            updated[idx] = e.target.value;
                            setTempStudentDetails((prev) => ({
                              ...prev,
                              certificates: updated,
                            }));
                          }}
                        />
                        {tempStudentDetails.certificates.length > 1 && (
                          <button
                            type="button"
                            className="remove-item-btn"
                            onClick={() => {
                              const updated =
                                tempStudentDetails.certificates.filter(
                                  (_, i) => i !== idx
                                );
                              setTempStudentDetails((prev) => ({
                                ...prev,
                                certificates: updated,
                              }));
                            }}
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="edit-field full-width">
                    <div className="education-field-header">
                      <label>Projects</label>
                      <button
                        type="button"
                        className="add-item-btn"
                        onClick={() => {
                          if (tempStudentDetails.projects.length < 5) {
                            setTempStudentDetails((prev) => ({
                              ...prev,
                              projects: [...prev.projects, ""],
                            }));
                          }
                        }}
                      >
                        + Add
                      </button>
                    </div>
                    {tempStudentDetails.projects.map((proj, idx) => (
                      <div key={idx} className="input-with-remove">
                        <textarea
                          className="education-edit-textarea"
                          placeholder="e.g., E-commerce Website with React"
                          value={proj}
                          rows={2}
                          onChange={(e) => {
                            const updated = [...tempStudentDetails.projects];
                            updated[idx] = e.target.value;
                            setTempStudentDetails((prev) => ({
                              ...prev,
                              projects: updated,
                            }));
                          }}
                        />
                        {tempStudentDetails.projects.length > 1 && (
                          <button
                            type="button"
                            className="remove-item-btn"
                            onClick={() => {
                              const updated =
                                tempStudentDetails.projects.filter(
                                  (_, i) => i !== idx
                                );
                              setTempStudentDetails((prev) => ({
                                ...prev,
                                projects: updated,
                              }));
                            }}
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              /* View Mode */
              <>
                <div className="student-info-grid">
                  <div className="student-info-item">
                    <span className="student-info-label">
                      <GraduationCap size={14} /> Education Level
                    </span>
                    <span className="student-info-value">
                      {educationLevelMap[studentDetails.educationLevel] ||capitalizeFirstLetter( universityDetails?.degree )|| "-"}
                    </span>
                  </div>
                  <div className="student-info-item">
                    <span className="student-info-label">
                      <BookOpen size={14} /> Department
                    </span>
                    <span className="student-info-value">
                      {departmentMap[studentDetails.department] ||capitalizeFirstLetter(universityDetails?.branch) || "-"}
                    </span>
                  </div>
                  <div className="student-info-item">
                    <span className="student-info-label">
                      <Award size={14} /> CGPA
                    </span>
                    <span className="student-info-value highlight">
                    {studentDetails.cgpa ? `${studentDetails.cgpa}/10` : "-"}
                    </span>
                  </div>
                  <div className="student-info-item">
                    <span className="student-info-label">
                      <Target size={14} /> Year of Graduation
                    </span>
                    <span className="student-info-value">
                      {studentDetails.yearOfGraduation ||universityDetails?.year || "-"}
                    </span>
                  </div>
                  <div className="student-info-item">
                    <span className="student-info-label">
                      <Briefcase size={14} /> Current Semester
                    </span>
                    <span className="student-info-value">
                      {studentDetails.currentSemester ||capitalizeFirstLetter(universityDetails?.semester)  ||"-"}
                    </span>
                  </div>
                </div>

                <div className="student-info-grid">
                  {((studentDetails.skills?.length > 0 &&
                    studentDetails.skills.some((s) => s)) ||
                    (tempStudentDetails.skills?.length > 0 &&
                      tempStudentDetails.skills.some((s) => s))) &&
                    !isEditing && (
                      <div className="student-section">
                        <div className="section-title">
                          <Code size={16} />
                          <span>Skills</span>
                        </div>
                        <div className="tags-container">
                          {(studentDetails.skills || [])
                            .filter((s) => s)
                            .map((skill, idx) => (
                              <span key={idx} className="tag skill">
                                {skill}
                              </span>
                            ))}
                        </div>
                      </div>
                    )}

                  {studentDetails.certificates?.length > 0 &&
                    studentDetails.certificates.some((c) => c) && (
                      <div className="student-section">
                        <div className="section-title">
                          <Award size={16} />
                          <span>Certificates</span>
                        </div>
                        <div className="tags-container">
                          {studentDetails.certificates
                            .filter((c) => c)
                            .map((cert, idx) => (
                              <span key={idx} className="tag certificate">
                                {cert}
                              </span>
                            ))}
                        </div>
                      </div>
                    )}
                </div>

                {studentDetails.projects?.length > 0 &&
                  studentDetails.projects.some((p) => p) && (
                    <div className="student-section">
                      <div className="section-title">
                        <FolderGit2 size={16} />
                        <span>Projects</span>
                      </div>
                      <div className="projects-list">
                        {studentDetails.projects
                          .filter((p) => p)
                          .map((project, idx) => (
                            <div key={idx} className="project-item">
                              {project.charAt(0).toUpperCase() + project.slice(1)}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
              </>
            )}
          </div>
        )}

        {/* Working Professional - Skills Section */}
        {!isStudent && (
          <div className="profile-card">
            <div className="profile-card-header">
              <Code size={20} />
              <h3>Skills</h3>
            </div>
            {isEditing ? (
              <div className="editable-tags">
                <input
                  type="text"
                  value={skillInput}
                  onChange={(e) => {
                    setSkillInput(e.target.value);
                    setSkillError("");
                  }}
                  onKeyDown={handleSkillAdd}
                  placeholder="Type skill and press Enter"
                  className="profile-tag-input"
                />
                {skillError && <span className="tag-error">{skillError}</span>}
                <div className="tags-container editable">
                  {tempSkills.map((skill, idx) => (
                    <span key={idx} className="tag skill">
                      {skill}
                      <X
                        size={12}
                        onClick={() =>
                          setTempSkills(tempSkills.filter((_, i) => i !== idx))
                        }
                      />
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="tags-container">
                {skills.length > 0 ? (
                  skills.map((skill, idx) => (
                    <span key={idx} className="tag skill">
                      {skill}
                    </span>
                  ))
                ) : (
                  <span className="empty-state">No skills added yet</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Working Professional - Target Roles Section */}
        {!isStudent && (
          <div className="profile-card">
            <div className="profile-card-header">
              <Target size={20} />
              <h3>Target Roles</h3>
            </div>
            {isEditing ? (
              <div className="editable-tags">
                <input
                  type="text"
                  value={roleInput}
                  onChange={(e) => {
                    setRoleInput(e.target.value);
                    setRoleError("");
                  }}
                  onKeyDown={handleRoleAdd}
                  placeholder="Type role and press Enter"
                  className="profile-tag-input"
                />
                {roleError && <span className="tag-error">{roleError}</span>}
                <div className="tags-container editable">
                  {tempRoles.map((role, idx) => (
                    <span key={idx} className="tag role">
                      {role}
                      <X
                        size={12}
                        onClick={() =>
                          setTempRoles(tempRoles.filter((_, i) => i !== idx))
                        }
                      />
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="tags-container">
                {targetRoles.length > 0 ? (
                  targetRoles.map((role, idx) => (
                    <span key={idx} className="tag role">
                      {role}
                    </span>
                  ))
                ) : (
                  <span className="empty-state">No target roles added yet</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Working Professional - Education Details Section */}
        {/* Working Professional - Education Details Section */}
        {!isStudent && (
          <div className="profile-card work-experience-card">
            <div className="profile-card-header">
              <GraduationCap size={20} />
              <h3>Education Details</h3>
            </div>
            {isEditing ? (
              <div className="edit-fields-grid">
                <div className="edit-field">
                  <CustomSelect
                    label="Highest Degree"
                    value={tempEducationLevel}
                    placeholder="Select degree"
                    options={[
                      { value: "high_school", label: "High School" },
                      { value: "bachelors", label: "Bachelor's" },
                      { value: "masters", label: "Master's" },
                      { value: "phd", label: "PhD" },
                    ]}
                    onChange={(val) => setTempEducationLevel(val)}
                  />
                </div>
                <div className="edit-field">
                  <label>Major / Department</label>
                  <input
                    type="text"
                    className="edit-input"
                    placeholder="e.g., Computer Science"
                    value={tempMajor}
                    onChange={(e) => setTempMajor(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <div className="work-experience-view">
                {profileData?.education_level || profileData?.major ? (
                  <div className="work-experience-grid">
                    <div className="work-experience-item">
                      <span className="work-experience-label">
                        <GraduationCap size={14} /> Highest Degree
                      </span>
                      <span className="work-experience-value">
                        {educationLevelMap[profileData?.education_level] || profileData?.education_level || "-"}
                      </span>
                    </div>
                    <div className="work-experience-item">
                      <span className="work-experience-label">
                        <BookOpen size={14} /> Major / Department
                      </span>
                      <span className="work-experience-value">
                        {profileData?.major?.charAt(0).toUpperCase() + profileData?.major?.slice(1) || "-"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <span className="empty-state">No education details added yet</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Working Professional - Work Experience Section */}
        {!isStudent && (
          <div className="profile-card work-experience-card">
            <div className="profile-card-header">
              <Briefcase size={20} />
              <h3>Work Experience</h3>
            </div>
            {isEditing ? (
              <div className="edit-fields-grid">
                <div className="edit-field">
                  <label>Current Job Title</label>
                  <input
                    type="text"
                    className="edit-input"
                    placeholder="e.g., Senior Software Engineer"
                    value={tempWorkingDetails.currentJobTitle}
                    onChange={(e) =>
                      setTempWorkingDetails((prev) => ({
                        ...prev,
                        currentJobTitle: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="edit-field">
                  <label>Current Company</label>
                  <input
                    type="text"
                    className="edit-input"
                    placeholder="e.g., Google, Microsoft"
                    value={tempWorkingDetails.currentCompany}
                    onChange={(e) =>
                      setTempWorkingDetails((prev) => ({
                        ...prev,
                        currentCompany: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="edit-field">
                  <label>Years of Experience</label>
                  <input
                    type="number"
                    className="edit-input"
                    placeholder="e.g., 5"
                    min="0"
                    step="0.5"
                    value={tempWorkingDetails.totalExperience}
                    onChange={(e) =>
                      setTempWorkingDetails((prev) => ({
                        ...prev,
                        totalExperience: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            ) : (
              <div className="work-experience-view">
                {profileData?.working_professional_details?.currentJobTitle ||
                profileData?.working_professional_details?.currentCompany ||
                profileData?.working_professional_details?.totalExperience ? (
                  <div className="work-experience-grid">
                    <div className="work-experience-item">
                      <span className="work-experience-label">
                        <Briefcase size={14} /> Current Role
                      </span>
                      <span className="work-experience-value">
                        {profileData?.working_professional_details?.currentJobTitle?.charAt(0).toUpperCase() + profileData?.working_professional_details?.currentJobTitle?.slice(1) || "-"}
                      </span>
                    </div>
                    <div className="work-experience-item">
                      <span className="work-experience-label">
                        <Building2 size={14} /> Company
                      </span>
                      <span className="work-experience-value">
                        {profileData?.working_professional_details?.currentCompany?.charAt(0).toUpperCase() + profileData?.working_professional_details?.currentCompany?.slice(1) || "-"}
                      </span>
                    </div>
                    <div className="work-experience-item">
                      <span className="work-experience-label">
                        <Award size={14} /> Experience
                      </span>
                      <span className="work-experience-value highlight">
                        {profileData?.working_professional_details?.totalExperience
                          ? `${profileData.working_professional_details.totalExperience} year${
                              profileData.working_professional_details.totalExperience !== "1" ? "s" : ""
                            }`
                          : "-"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <span className="empty-state">No work experience added yet</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyAccountModal;